import { Theme, lighten } from '@mui/material'

import makeStyles from '@mui/styles/makeStyles'

type StylesType = {
  middleMsg: boolean
  flip: boolean
  isYours: boolean
  smallMedia: boolean
  startGroup: boolean
  endGroup: boolean
  emojiCount: number
}

const borderRadius = '0.705rem'

const getMessageFontSize = ({ emojiCount }: StylesType, theme: Theme) => {
  const base = theme.size.sm

  if (emojiCount === 0 || emojiCount >= 5) {
    return base
  }

  if (emojiCount === 1) {
    return `calc(3 * ${base})`
  }

  if (emojiCount === 2) {
    return `calc(2 * ${base})`
  }

  if ([3, 4].includes(emojiCount)) {
    return `calc(1.5 * ${base})`
  }
}

const useStyles = makeStyles<Theme, StylesType>(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    margin: ({ middleMsg }) =>
      middleMsg ? '0 1.25rem' : '0.455rem 1.25rem 0.25rem 1.25rem',
    fontSize: theme.size.sm,
    '& blockquote': {
      backgroundColor: ({ flip }) =>
        flip ? 'white' : lighten(theme.palette.secondary.main, 0.6)
    }
  },

  row: {
    display: 'flex',
    flexDirection: ({ flip }) => (flip ? 'row-reverse' : 'row')
  },

  bottomRow: {
    marginLeft: ({ flip }) => (flip ? '0' : '58px'),
    marginRight: ({ flip }) => (flip ? '58px' : '0')
  },

  channels: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: ({ flip }) => (flip ? 'flex-end' : 'flex-start'),
    marginLeft: ({ flip }) => (flip ? '0' : '2px'),
    marginRight: ({ flip }) => (flip ? '2px' : '0'),
    fontSize: theme.size.sm,
    fontStyle: 'italic',
    color: '#7F8487',
    paddingBottom: '0.3125rem',

    '& > div': {
      display: 'flex',
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap'
    },

    '& .channel': {
      marginLeft: '0.125rem',
      alignItems: 'center'
    },

    '& .name': {
      marginLeft: '0.125rem'
    }
  },

  avatar: {
    margin: '0 10px',
    width: '2rem',
    height: '2rem',
    textTransform: 'capitalize',
    fontSize: theme.size.sm,
    padding: '0.125rem',
    '& > img ': {
      borderRadius: '100%'
    }
  },

  messageBase: {
    maxWidth: '75%',

    // Full width on mobile
    [theme.breakpoints.down(767)]: {
      maxWidth: '100%'
    },

    '& .box': {
      position: 'relative',
      border: '2px solid',
      borderRadius,
      boxSizing: 'border-box',
      padding: '0.705rem',
      wordWrap: 'anywhere',
      fontSize: props => getMessageFontSize(props, theme)
    },

    '& .box > img': {
      marginTop: '0.5rem',
      width: '100%',
      height: 'auto'
    },

    '& .footer': {
      display: 'flex',
      padding: '0.3125rem',
      fontSize: theme.size.sm,
      color: '#7F8487'
    },

    '& .footer .info': {
      display: 'flex',
      alignItems: 'center'
    },

    '& .footer .info .name': {
      marginRight: '7px'
    },

    '& .footer .info .rest': {
      display: 'flex',
      alignItems: 'center',

      '& > div': {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer'
      }
    }
  },

  message: {
    '& .box': {
      borderColor: '#F1F1F1',
      borderTopRightRadius: ({ flip }) => (flip ? '0' : borderRadius),
      borderTopLeftRadius: ({ flip }) => (flip ? borderRadius : '0'),
      backgroundColor: ({ isYours }) =>
        isYours ? theme.palette.primary.main : 'white',
      color: ({ flip }) => (flip ? 'white' : 'black')
    },

    '& .footer': {
      flexDirection: ({ flip }) => (flip ? 'row-reverse' : 'row')
    }
  },

  note: {
    '& .box': {
      borderColor: theme.palette.secondary.main,
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.primary.main
    },

    '& .footer': {
      flexDirection: 'row-reverse'
    }
  },

  icon: {
    marginRight: '5px',
    width: '0.65rem',
    height: '0.65rem'
  },

  dots: {
    margin: ({ flip }) => (flip ? '0.5rem 0.25rem 0 0' : '0.5rem 0 0 0.25rem'),
    height: 'max-content',
    width: 'max-content',

    '& .dot': {
      cursor: 'pointer',

      '&:hover': {
        color: theme.palette.primary.main
      }
    }
  },

  emojiAction: {
    color: 'black',
    '&:hover': {
      color: theme.palette.primary.main
    },
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },

  ticks: {
    position: 'relative',
    margin: '0.25rem 0 0 0',
    display: 'flex',
    justifyContent: 'center',
    width: '1rem'
  },

  topTick: {
    position: 'absolute',
    marginLeft: ({ smallMedia }) => (smallMedia ? '5px' : '10px')
  },

  bottomTick: {
    position: 'relative'
  },

  errorMsg: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingRight: '2px',
    color: '#7F8487'
  },

  retry: {
    cursor: 'pointer'
  },

  msgContent: {
    whiteSpace: 'break-spaces',

    '& span': {
      margin: '0 1px',
      padding: '0.15rem 0.5rem',
      borderRadius: borderRadius
    }
  },

  middleMsg: {
    '& .box': {
      borderRadius: ({ middleMsg, flip }) =>
        middleMsg && flip ? `0.705rem 0 0 0.705rem` : `0 0.705rem 0.705rem 0`
    }
  },

  start: {
    marginBottom: ({ startGroup }) => startGroup && '0',

    '& .box': {
      borderBottomRightRadius: ({ flip }) =>
        flip ? '0 !important' : borderRadius,
      borderBottomLeftRadius: ({ flip }) => (flip ? borderRadius : '0'),
      borderTopRightRadius: `${borderRadius} !important`,
      borderTopLeftRadius: `${borderRadius} !important`
    }
  },

  end: {
    marginTop: ({ endGroup }) => endGroup && '0',

    '& .box': {
      borderTopRightRadius: ({ flip }) =>
        flip ? '0 !important' : borderRadius,
      borderTopLeftRadius: ({ flip }) => (flip ? borderRadius : '0'),
      borderBottomRightRadius: `${borderRadius} !important`,
      borderBottomLeftRadius: `${borderRadius} !important`
    }
  },

  edited: {
    marginLeft: '0.25rem',
    fontSize: theme.size.sm,
    fontStyle: 'italic',
    opacity: '0.8'
  },

  mention: {
    background: theme.palette.avatarBackground,
    color: theme.palette.textLight
  },

  mentionYou: {
    background: theme.palette.primary.main,
    color: '#FFFFFF'
  },

  reactionsRoot: {
    cursor: 'default',
    position: 'relative',
    marginTop: 2,
    marginBottom: 2,
    zIndex: 1,
    gap: 2,
    display: 'inline-flex'
  },

  tooltip: {
    fontSize: theme.size.sm
  }
}))

export default useStyles
